'use client';

import { useSettingsContext } from '@/components/settings';
import { billingLevels, bottomTiers, topTiers } from '@/utils/constants';
import { formatMoney } from '@/utils/currency';
import { Box } from '@mui/material';
import { keyframes } from '@mui/system';
import confetti from 'canvas-confetti';
import { ChevronRight } from 'lucide-react';
import PropTypes from 'prop-types';
import { useEffect, useMemo, useRef, useState } from 'react';

const glow = keyframes`
  from { filter: drop-shadow(0 0 4px var(--glow)); }
  to { filter: drop-shadow(0 0 12px var(--glow)); }
`;

const TIERS = [...bottomTiers, ...topTiers];

export function BillingCard({
  totalSales,
  nextAward,
  previousTotalSales = 0,
  triggerLoginEffect = false,
}) {
  const [isBlinking, setIsBlinking] = useState(false);
  const [shownLevels, setShownLevels] = useState(new Set());
  const previousLevelRef = useRef(null);
  const initialLoadRef = useRef(true);
  const { themeMode } = useSettingsContext();

  const MOCK_LEVEL_INDEX = null;
  const currentLevel = useMemo(() => {
    if (MOCK_LEVEL_INDEX !== null && billingLevels[MOCK_LEVEL_INDEX]) {
      return billingLevels[MOCK_LEVEL_INDEX];
    }

    return (
      billingLevels.find(
        (level) =>
          totalSales >= level.min_value &&
          (level.max_value === null || totalSales < level.max_value)
      ) || billingLevels[0]
    );
  }, [totalSales]);

  const tier = TIERS.find((item) => item.code === currentLevel.code) || TIERS[0];
  const percent = nextAward ? Math.min(100, Math.max(0, (totalSales / nextAward) * 100)) : 100;
  const remaining = Math.max(0, nextAward - totalSales);
  const brl = (value) => formatMoney(value, 'BRL');

  const triggerConfettiEffect = useMemo(
    () => (level, reason) => {
      setIsBlinking(true);

      const duration = reason === 'levelUp' ? 4000 : 3000;
      const end = Date.now() + duration;

      let colors;
      if (level.color.includes('red') || level.color.includes('pink')) {
        colors = ['#ef4444', '#f87171', '#fca5a5'];
      } else if (level.color.includes('emerald') || level.color.includes('green')) {
        colors = ['#10b981', '#34d399', '#6ee7b7'];
      } else if (level.color.includes('yellow') || level.color.includes('amber')) {
        colors = ['#f59e0b', '#fbbf24', '#fcd34d'];
      } else if (level.color.includes('cyan') || level.color.includes('blue')) {
        colors = ['#06b6d4', '#22d3ee', '#67e8f9'];
      } else if (level.color.includes('gray') || level.color.includes('slate')) {
        colors = ['#6b7280', '#9ca3af', '#d1d5db'];
      } else {
        colors = ['#8b5cf6', '#a78bfa', '#c4b5fd'];
      }

      const frame = () => {
        const particleCount = reason === 'levelUp' ? 4 : 2;

        confetti({
          particleCount,
          angle: 60,
          spread: 55,
          origin: { x: 0 },
          colors,
        });
        confetti({
          particleCount,
          angle: 120,
          spread: 55,
          origin: { x: 1 },
          colors,
        });

        if (reason === 'levelUp') {
          confetti({
            particleCount: 3,
            angle: 90,
            spread: 45,
            origin: { x: 0.5, y: 0.6 },
            colors,
          });
        }

        if (Date.now() < end) {
          requestAnimationFrame(frame);
        }
      };

      frame();

      setTimeout(() => setIsBlinking(false), reason === 'levelUp' ? 3000 : 2000);
    },
    []
  );

  useEffect(() => {
    const hasLevelChanged =
      previousLevelRef.current && previousLevelRef.current.name !== currentLevel.name;
    const hasProgressIncreased = totalSales > previousTotalSales;
    const isNewLevel = !shownLevels.has(currentLevel.name);

    if (hasLevelChanged && hasProgressIncreased && isNewLevel) {
      console.log(
        '[v0] Level up detected:',
        previousLevelRef.current?.name,
        '->',
        currentLevel.name
      );
      triggerConfettiEffect(currentLevel, 'levelUp');
      setShownLevels((prev) => new Set([...prev, currentLevel.name]));
    }

    previousLevelRef.current = currentLevel;
  }, [totalSales, previousTotalSales, currentLevel, shownLevels, triggerConfettiEffect]);

  useEffect(() => {
    if (triggerLoginEffect && totalSales > 0 && initialLoadRef.current) {
      const isNewLevel = !shownLevels.has(currentLevel.name);

      if (isNewLevel) {
        console.log('[v0] Login effect triggered for new level:', currentLevel.name);
        triggerConfettiEffect(currentLevel, 'login');
        setShownLevels((prev) => new Set([...prev, currentLevel.name]));
      }

      initialLoadRef.current = false;
    }
  }, [triggerLoginEffect, currentLevel, totalSales, triggerConfettiEffect, shownLevels]);

  const isDark = themeMode === 'dark';
  const muted = isDark ? 'rgba(255,255,255,0.5)' : 'rgba(22,28,36,0.56)';

  return (
    <Box
      className={isBlinking ? 'animate-pulse' : ''}
      sx={{
        position: 'relative',
        width: '100%',
        pt: 1.25,
        pb: 1.25,
        px: 2,
        borderRadius: '18px',
        border: '1px solid',
        borderColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(22,28,36,0.08)',
        bgcolor: isDark ? '#111315' : '#FFFFFF',
        color: isDark ? '#FFFFFF' : '#161C24',
        boxShadow: isDark ? '0 10px 24px rgba(0,0,0,0.28)' : '0 8px 20px rgba(22,28,36,0.08)',
      }}
    >
      <Box
        component="img"
        src={tier.image}
        alt={`Cacto ${tier.name}`}
        sx={{
          '--glow': `${tier.accent}aa`,
          position: 'absolute',
          top: 4,
          right: 14,
          width: 40,
          height: 40,
          objectFit: 'contain',
          animation: `${glow} 1.8s ease-in-out infinite alternate`,
          '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
        }}
      />

      <Box sx={{ pr: 6.5, fontSize: 14, fontWeight: 500, opacity: 0.8 }}>Faturamento</Box>

      <Box sx={{ mt: 1.25, display: 'flex', alignItems: 'flex-end', gap: 1.25 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box
            sx={{
              fontSize: 18,
              fontWeight: 700,
              lineHeight: 1.25,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {brl(totalSales)}
          </Box>
          {nextAward > 0 && (
            <Box sx={{ mt: '2px', fontSize: 12, color: muted }}>de {brl(nextAward)}</Box>
          )}
        </Box>
        <Box sx={{ textAlign: 'right', flexShrink: 0 }}>
          <Box sx={{ fontSize: 16, fontWeight: 700, color: tier.accent, lineHeight: 1.2 }}>
            {Math.round(percent)}%
          </Box>
          <Box sx={{ fontSize: 11, color: muted }}>da meta</Box>
        </Box>
      </Box>

      <Box
        sx={{
          mt: 1.5,
          height: 8,
          borderRadius: 999,
          overflow: 'hidden',
          bgcolor: isDark ? 'rgba(255,255,255,0.14)' : 'rgba(22,28,36,0.10)',
        }}
      >
        <Box
          sx={{
            height: 1,
            width: `${percent}%`,
            borderRadius: 999,
            background: `linear-gradient(90deg, ${tier.progressColors.join(', ')})`,
            transition: 'width 1s ease-out',
          }}
        />
      </Box>

      <Box
        sx={{
          mt: 1.5,
          height: '1px',
          bgcolor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(22,28,36,0.08)',
        }}
      />

      <Box sx={{ mt: 1.25, display: 'flex', alignItems: 'center', gap: 1.25 }}>
        <Box sx={{ flex: 1, fontSize: 11, lineHeight: '16px', fontWeight: 500, color: muted }}>
          {nextAward > 0 ? (
            <>
              Faltam{' '}
              <Box component="span" sx={{ fontWeight: 700, color: tier.accent }}>
                {brl(remaining)}
              </Box>{' '}
              para o próximo nível
            </>
          ) : (
            'Você chegou ao topo da jornada'
          )}
        </Box>
        <ChevronRight size={20} strokeWidth={1.8} color={muted} />
      </Box>
    </Box>
  );
}

BillingCard.propTypes = {
  totalSales: PropTypes.number,
  nextAward: PropTypes.number,
  previousTotalSales: PropTypes.number,
  triggerLoginEffect: PropTypes.bool,
};
